import { Injectable } from '@angular/core';

/**
 * Ponte tipada para o `window.clarity` — o gravador de sessão da Microsoft.
 *
 * O SNIPPET NÃO MORA AQUI. Ele é um bloco inline no fim do `src/index.html`, com
 * três guardas (produção pelo hostname EXATO, `Save-Data` desligado, aba visível
 * na hora do disparo) e o carregamento adiado para o primeiro gesto. O porquê de
 * cada uma está em `docs/clarity.md`; o gate que as executa é o `check:clarity`.
 *
 * O que interessa para quem chama daqui: `window.clarity` existe SEMPRE depois do
 * index — é um stub de fila em produção e um no-op de verdade fora dela. Chamar é
 * barato e seguro em qualquer ambiente, e nada se perde por vir antes do SDK: o
 * carregador oficial drena `clarity.q` ao chegar. A guarda abaixo cobre só os
 * mundos onde o index não rodou: SSR (não há `window`) e Karma (o spec carrega o
 * bundle, não o HTML).
 */

/** Eventos que valem uma gravação garantida — ver `upgrade()`. */
const EVENTOS_DE_CONVERSAO = new Set(['checkout_submitted', 'purchase']);

/**
 * Faixa do valor do pedido, para a tag `valor_pedido`.
 *
 * Valor cru viraria uma tag por centavo: o filtro do painel lista os valores
 * distintos de cada chave, e com isso ele fica inutilizável. Faixa mantém a
 * pergunta que importa ("quero ver quem fechou pedido grande") respondível com
 * meia dúzia de opções na lista.
 */
export function faixaDeValor(valor: number): string {
	if (!isFinite(valor) || valor < 0) return 'desconhecido';
	if (valor < 30) return '0-30';
	if (valor < 60) return '30-60';
	if (valor < 100) return '60-100';
	if (valor < 200) return '100-200';
	return '200+';
}

@Injectable({ providedIn: 'root' })
export class ClarityService {
	private get api(): ((...args: any[]) => void) | null {
		if (typeof window === 'undefined') return null;
		const fn = (window as any).clarity;
		return typeof fn === 'function' ? fn : null;
	}

	/**
	 * Custom tag — o que vira FILTRO de gravação no painel.
	 * Chamar a mesma chave mais de uma vez acumula valores (a tag é multivalor).
	 */
	tag(chave: string, valor: string | string[]): void {
		const api = this.api;
		if (!api || !chave || valor === undefined || valor === null || valor === '') return;
		api('set', chave, valor);
	}

	/** Custom event — aparece junto dos Smart Events, em Filtros e no dashboard. */
	event(nome: string): void {
		const api = this.api;
		if (!api || !nome) return;
		api('event', nome);
	}

	/**
	 * Tira a sessão da amostragem.
	 *
	 * O Clarity guarda até 100 mil gravações por projeto por dia e, passando
	 * disso, começa a descartar sessões. Filtrar por "finalizou pedido" não vale
	 * nada se justamente a gravação do pedido foi a sorteada para sumir — e
	 * conversão é fatia pequena do tráfego, então marcar o funil inteiro não
	 * seria proteção nenhuma, só reintroduziria o sorteio. Por isso só os dois
	 * eventos de fim de funil sobem.
	 */
	upgrade(motivo: string): void {
		const api = this.api;
		if (!api || !motivo) return;
		api('upgrade', motivo);
	}

	/**
	 * Carimba a sessão inteira com a loja. Roda uma vez, no `init` do funil.
	 *
	 * `restaurant_id` é o ID numérico — a mesma chave e o mesmo valor que vão nas
	 * propriedades globais do OpenPanel, para uma pergunta feita lá poder ser
	 * reaberta aqui sem tradução. A tag `loja` (slug da URL) já é posta pelo
	 * index; as duas convivem porque nem todo mundo procura a loja pelo mesmo
	 * lado — quem vem do banco tem o ID, quem vem do link tem o slug.
	 */
	identificarLoja(restaurantId: number, flowType: string): void {
		if (!restaurantId) return;
		this.tag('restaurant_id', String(restaurantId));
		this.tag('flow_type', flowType);
	}

	/**
	 * Espelha um evento do funil do OpenPanel.
	 *
	 * Chamado de um lugar só — `OpenPanelService.track()` —, que é o que mantém
	 * os dois painéis sincronizados por construção: evento novo lá aparece aqui
	 * sem ninguém lembrar de nada. Ver a nota no `openPanel.service.ts`.
	 *
	 * Vão três coisas por evento:
	 *   - o custom event, com o MESMO nome do OpenPanel (`add_to_cart`…);
	 *   - a tag `funil`, multivalor, que acumula as etapas alcançadas na sessão
	 *     (dá o filtro "chegou em X" sem depender da lista de eventos);
	 *   - `valor_pedido` em faixa, e `upgrade`, nos eventos de conversão.
	 *
	 * A faixa sai SÓ na conversão de propósito. Todos os eventos do funil levam
	 * `value`, e como a tag é multivalor, espelhar todos deixaria `valor_pedido`
	 * com o carrinho pela metade ao lado do total — a chave diria uma coisa e
	 * listaria outra.
	 */
	eventoDoFunil(nome: string, propriedades: { [key: string]: any } = {}): void {
		if (!this.api || !nome) return;

		this.event(nome);
		this.tag('funil', nome);

		if (!EVENTOS_DE_CONVERSAO.has(nome)) return;

		const valor = propriedades ? propriedades['value'] : undefined;
		if (typeof valor === 'number') this.tag('valor_pedido', faixaDeValor(valor));
		this.upgrade(nome);
	}
}
